import { Component, type ReactNode } from 'react'
import { useRouteError, isRouteErrorResponse } from 'react-router-dom'

/**
 * Top-level error boundary. Catches render errors from unstable
 * React reconciliation (e.g. `removeChild` failures when third-party
 * DOM — TipTap node views, Floating UI, view transitions — ends up
 * out of sync with React's tree). Instead of a white screen, the
 * user gets a recover affordance.
 *
 * In dev we also dump the full stack + componentStack — without that
 * we just see `error.message` which is rarely enough to act on.
 */
type State = { error: Error | null; componentStack: string | null }

const isDev = import.meta.env.DEV

export class ErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: null }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null })
    // eslint-disable-next-line no-console
    console.error('[app] render error', error, info)
  }

  reset = () => this.setState({ error: null, componentStack: null })

  render() {
    if (!this.state.error) return this.props.children
    return (
      <ErrorScreen
        error={this.state.error}
        componentStack={this.state.componentStack}
        onReset={this.reset}
      />
    )
  }
}

/**
 * Route-level error element. React Router catches route render
 * errors before the top-level boundary can see them and renders its
 * own stock "💿 Hey developer" fallback — attach this to every
 * route via `errorElement` so we get the real stack instead.
 */
export function RouteErrorElement() {
  const error = useRouteError()
  const e =
    error instanceof Error
      ? error
      : isRouteErrorResponse(error)
        ? new Error(`${error.status} ${error.statusText}`)
        : new Error(
            typeof error === 'string'
              ? error
              : JSON.stringify(error, null, 2),
          )
  if (isDev) {
    // eslint-disable-next-line no-console
    console.error('[route] render error', error)
  }
  return (
    <ErrorScreen
      error={e}
      componentStack={null}
      onReset={() => window.location.reload()}
    />
  )
}

function ErrorScreen({
  error,
  componentStack,
  onReset,
}: {
  error: Error
  componentStack: string | null
  onReset: () => void
}) {
  // If the crash happened on a doc page, offer a raw-markdown view
  // so the user can save their work even if the editor keeps
  // failing to mount. Match /d/<id> (with optional ?share=...).
  const docMatch =
    typeof window !== 'undefined'
      ? window.location.pathname.match(/^\/d\/([A-Za-z0-9-]+)(?:\/raw)?$/)
      : null
  const rawHref = docMatch
    ? `/d/${docMatch[1]}/raw${window.location.search}`
    : null

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6 text-center">
      <div className="w-full max-w-[720px]">
        <div className="mb-2 font-serif text-[22px] text-ink">
          something slipped
        </div>
        <div className="mb-5 font-serif text-[14px] italic leading-relaxed text-ink-muted">
          the page hit an error. your document is safe.
          {rawHref && (
            <>
              {' '}
              open the{' '}
              <a
                href={rawHref}
                className="font-mono text-[12px] not-italic text-ink underline underline-offset-2 hover:text-[color:var(--color-accent)]"
              >
                raw markdown view
              </a>{' '}
              if you want to copy your text out.
            </>
          )}
        </div>
        <pre className="mb-3 max-h-40 overflow-auto rounded border border-border bg-paper p-2 text-left font-mono text-[11px] text-ink-muted">
          {error.message}
        </pre>
        {isDev && error.stack && (
          <details
            open
            className="mb-3 rounded border border-border bg-paper p-2 text-left"
          >
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.1em] text-ink-muted">
              stack
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto font-mono text-[10px] leading-snug text-ink-muted">
              {error.stack}
            </pre>
          </details>
        )}
        {isDev && componentStack && (
          <details
            open
            className="mb-5 rounded border border-border bg-paper p-2 text-left"
          >
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.1em] text-ink-muted">
              component stack
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto font-mono text-[10px] leading-snug text-ink-muted">
              {componentStack}
            </pre>
          </details>
        )}
        <div className="flex justify-center gap-2">
          <button
            onClick={onReset}
            className="rounded border border-border bg-paper px-3 py-1.5 text-[12px] text-ink transition-colors hover:bg-surface-hover"
          >
            try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded px-3 py-1.5 text-[12px] text-paper"
            style={{ backgroundColor: 'var(--color-accent)' }}
          >
            reload
          </button>
        </div>
      </div>
    </div>
  )
}
